import { placeHolderData } from '../../config';
import { getAlbumById, getTrackById } from '../../services/api';
import { getCreationTrack } from '../../services/myCreationsApi';
import { getTrackUrlForLocale } from '../../utils/localeUtils';
import { isStationModeEnabled } from './helpers';

export interface PlayParams {
  albumId?: string;
  trackId?: string;
}

export interface PlayCreationParams {
  orgId: string;
  creationId: string;
}

export interface PlayResult {
  success: boolean;
  error?: string;
}

type SetActivePlaylistFunction = (playlist: any) => void;
type SetActivePopupFunction = (popup: string) => void;
type AddRecentTrackFunction = (track: any) => void;
type GetPreferredLocaleFunction = () => string | undefined;
type SetVolumeFunction = (volume: number) => void;
type GetVolumeFunction = () => number;

let setActivePlaylistFunction: SetActivePlaylistFunction | null = null;
let setActivePopupFunction: SetActivePopupFunction | null = null;
let addRecentTrackFunction: AddRecentTrackFunction | null = null;
let getPreferredLocaleFunction: GetPreferredLocaleFunction | null = null;
let setVolumeFunction: SetVolumeFunction | null = null;
let getVolumeFunction: GetVolumeFunction | null = null;

// Store original volume before ducking
let originalVolumeBeforeDucking: number | null = null;
const DUCKED_VOLUME = 1.0; // Volume level when ducked (20%)

export const setPlaybackFunctions = (
  setActivePlaylist: SetActivePlaylistFunction,
  setActivePopup: SetActivePopupFunction,
  addRecentTrack: AddRecentTrackFunction,
  getPreferredLocale: GetPreferredLocaleFunction,
  setVolume: SetVolumeFunction,
  getVolume: GetVolumeFunction,
) => {
  setActivePlaylistFunction = setActivePlaylist;
  setActivePopupFunction = setActivePopup;
  addRecentTrackFunction = addRecentTrack;
  getPreferredLocaleFunction = getPreferredLocale;
  setVolumeFunction = setVolume;
  getVolumeFunction = getVolume;
};

export const playTrackOrAlbum = async (
  params: PlayParams,
): Promise<PlayResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!setActivePlaylistFunction || !setActivePopupFunction) {
    return {
      success: false,
      error: 'Playback functions not initialized',
    };
  }

  try {
    const { albumId, trackId } = params;

    if (!albumId && !trackId) {
      return {
        success: false,
        error: 'Either albumId or trackId is required',
      };
    }

    if (trackId && !albumId) {
      // Play single track
      const trackInfo = await getTrackById(trackId);
      if (!trackInfo) {
        return {
          success: false,
          error: 'Track not found',
        };
      }

      const preferredLocale = getPreferredLocaleFunction?.() || 'en-US';

      const trackWithUrls = {
        id: trackInfo.id,
        name: trackInfo.trackName,
        duration: 0,
        isLiked: trackInfo.isLiked ?? false,
        urls: trackInfo.audioUrl?.urls || [],
        url:
          trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
            ? undefined
            : trackInfo.audioUrl?.path +
              '/' +
              trackInfo.audioUrl?.fileName +
              '.mp3',
      };

      const singleTrackAlbum = {
        id: trackInfo?.album?.id,
        albumName: trackInfo.album.name,
        albumArtUrl:
          trackInfo?.album?.thumbnailUrl || trackInfo?.album?.albumArtUrl,
        contributors: trackInfo.contributors,
        tracks: [trackWithUrls],
        category: 'favorites',
        likes: 0,
        isLiked: trackInfo.isLiked ?? false,
        upc: '',
        pk: '',
        description: '',
        genre: [],
        type: 'search',
      };

      const playlist = {
        currentIndex: 0,
        album: singleTrackAlbum,
      };

      const contributor = trackInfo.contributors[0];
      if (addRecentTrackFunction && contributor) {
        const recentTrack = {
          track: {
            name: trackInfo.trackName,
            url: getTrackUrlForLocale(trackWithUrls, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: contributor?.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            trackInfo?.album?.thumbnailUrl ||
            trackInfo?.album?.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrackFunction(recentTrack);
      }

      setActivePlaylistFunction(playlist);
      setActivePopupFunction('musicplayer');

      return {
        success: true,
      };
    } else if (albumId) {
      // Play album (optionally starting at a specific track)
      const albumData = await getAlbumById(albumId);
      if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
        return {
          success: false,
          error: 'Album not found or has no tracks',
        };
      }

      const preferredLocale = getPreferredLocaleFunction?.() || 'en-US';

      let currentIndex = 0;
      if (trackId) {
        const trackIndex = albumData.tracks.findIndex(
          (track: any) => track.id === trackId,
        );
        if (trackIndex !== -1) {
          currentIndex = trackIndex;
        }
      }

      const playlist = {
        currentIndex: currentIndex,
        album: albumData,
      };

      const contributor = albumData.contributors?.[0];
      const firstTrack = albumData.tracks[currentIndex];
      if (addRecentTrackFunction && contributor) {
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: currentIndex + 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData?.thumbnailUrl ||
            albumData?.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrackFunction(recentTrack);
      }

      setActivePlaylistFunction(playlist);
      setActivePopupFunction('musicplayer');

      return {
        success: true,
      };
    }

    return {
      success: false,
      error: 'Invalid parameters',
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to play track or album:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const playCreation = async (
  params: PlayCreationParams,
): Promise<PlayResult> => {
  if (!isStationModeEnabled()) {
    return {
      success: false,
      error: 'Station mode is not enabled',
    };
  }

  if (!setActivePlaylistFunction || !setActivePopupFunction) {
    return {
      success: false,
      error: 'Playback functions not initialized',
    };
  }

  try {
    const { orgId, creationId } = params;

    if (!orgId || !creationId) {
      return {
        success: false,
        error: 'Both orgId and creationId are required',
      };
    }

    const creation = await getCreationTrack(orgId, creationId);
    if (!creation || 'error' in creation) {
      return {
        success: false,
        error: 'Creation not found or has been deleted',
      };
    }

    const trackData = creation.data;

    const singleTrackAlbum = {
      id: creation.id,
      albumName: trackData.title || 'My Creation',
      albumArtUrl: trackData.albumThumbnail || trackData.previewImage || '',
      contributors: [
        {
          id: creation.contributor.id,
          givenName: creation.contributor.fullName,
          thumbnailUrl: creation.contributor.previewImage,
          videoDesktopUrl: creation.contributor.previewVideo || '',
          videoMobileUrl: creation.contributor.previewMobileVideo || '',
          slug: creation.contributor.slug,
          videoDesktopPreviewUrl: creation.contributor.previewVideoImage || '',
          videoMobilePreviewUrl:
            creation.contributor.previewMobileVideoImage || '',
        },
      ],
      tracks: [
        {
          id: creation.id,
          name: trackData.title,
          url: trackData.url,
          duration: trackData.duration || 0,
          lyrics: trackData.lyrics,
          lyricsTimestamped: trackData.lyricsTimestamped,
        },
      ],
      category: 'my-creations',
      likes: 0,
      isLiked: false,
      upc: creation.requestId,
      pk: '',
      description: creation.query,
      genre: trackData.genres || [],
      type: 'my-creations',
      orgId: orgId,
      creator: {
        id: creation.creator.id,
        fullName: creation.creator.fullName,
        email: creation.creator.email,
      },
    };

    const playlist = {
      currentIndex: 0,
      album: singleTrackAlbum,
    };

    if (addRecentTrackFunction) {
      const recentTrack = {
        track: {
          name: trackData.title,
          url: trackData.url,
          trackIndex: 1,
        },
        contributor: {
          id: creation.contributor.id,
          givenName: creation.contributor.fullName,
          thumbnailUrl: creation.contributor.previewImage,
          videoDesktopUrl: creation.contributor.previewVideo || '',
          videoMobileUrl: creation.contributor.previewMobileVideo || '',
          slug: creation.contributor.slug,
          videoDesktopPreviewUrl: creation.contributor.previewVideoImage || '',
          videoMobilePreviewUrl:
            creation.contributor.previewMobileVideoImage || '',
        },
        albumArtUrl: trackData.albumThumbnail || trackData.previewImage || '',
        timestamp: Date.now(),
      };
      addRecentTrackFunction(recentTrack);
    }

    setActivePlaylistFunction(playlist);
    setActivePopupFunction('musicplayer');

    return {
      success: true,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to play creation:', error);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const duckAudio = (shouldDuck: boolean): void => {
  if (!setVolumeFunction || !getVolumeFunction) {
    if (import.meta.env.DEV) {
      console.warn('Volume functions not initialized');
    }
    return;
  }

  if (shouldDuck) {
    // Duck: Save current volume and reduce it
    if (originalVolumeBeforeDucking === null) {
      originalVolumeBeforeDucking = getVolumeFunction();
      setVolumeFunction(DUCKED_VOLUME);
    }
  } else {
    // Restore: Return to original volume
    if (originalVolumeBeforeDucking !== null) {
      setVolumeFunction(originalVolumeBeforeDucking);
      originalVolumeBeforeDucking = null;
    }
  }
};
